import { useMutation, useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import Button from "components/base/Button";
import Money from "components/base/InputMoney";
import OrganizeFormCard from "components/cards/OrganizeFormCard";
import RegraFiscalEmpresaFormCard from "components/cards/RegraFiscalEmpresaFormCard";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import InventoryTab from "components/tabs/InventoryTab";
import HistoricoEstoqueTab from "components/tabs/HistoricoEstoqueTab";
import HistoricoVendasTab from "components/tabs/HistoricoVendasTab";
import HistoricoAlteracaoPrecoTab from "components/tabs/HistoricoAlteracaoPrecoTab";
import { EditarProdutoDtoType, ResultResponse, produtoBreadcrumbItems } from "data/produto";
import { floatFormat, transformErrors } from "helpers/utils";
import { ChangeEvent, useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Link, Navigate, useParams } from "react-router-dom";
import { usePermissionModal } from "contexts/PermissionModalContext";
import ProdutoEmpresaPrecosEspecificosTable from "components/tables/ProdutoEmpresaPrecosEspecificosTable";
import { produtoKeys, useProdutoInvalidation } from "hooks/useProduto";


const EditarProduto = () => {
  const { id } = useParams();
  const { modalState, getQueryRetryHandler, getMutationErrorHandler } = usePermissionModal();
  const { invalidateProduto } = useProdutoInvalidation();
  const produtoId = parseInt(id || "0");
  const [precoComDesconto, setPrecoComDesconto] = useState<number>(0);

  const { isLoading, isError, data } = useQuery<
    { data: EditarProdutoDtoType },
    AxiosError
  >({
    queryKey: produtoKeys.detail(produtoId),
    queryFn: () => axios.get(`/api/produto/${id}`),
    enabled: !!id,
    retry: getQueryRetryHandler({
      queryKey: produtoKeys.detail(produtoId),
      defaultMessage: "Você não possui permissão para visualizar este produto"
    }),
  });

  const mutation = useMutation<
    { data: ResultResponse<EditarProdutoDtoType> },
    any,
    { id: string; data: EditarProdutoDtoType }
  >({
    mutationFn: ({ id, data }) => axios.put(`/api/produto/${id}`, data),
    onSuccess: () => {
      // Invalida todas as queries relacionadas ao produto específico
      invalidateProduto(produtoId);
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para editar este produto",
      (error) => {
        // Tratamento de erros não relacionados à permissão
        // Check if response follows the Result pattern
        if (error.response?.data?.message) {
          // Handle Result pattern error
          form.setError('root.serverError', {
            type: 'manual',
            message: error.response.data.message
          });
        } else {
          // Handle traditional validation errors
          const formattedErrors = transformErrors(error.response?.data?.errors || {});
          for (const [field, error] of Object.entries(formattedErrors)) {
            form.setError(field as any, error as any);
          }
        }
      }
    ),
  });

  // With the Result pattern, product data is inside data.data (which contains isSuccess, message, and data properties)
  const produto = data?.data;


  const form = useForm<EditarProdutoDtoType>();

  useEffect(() => {
    if (produto) {

      form.reset(produto);

      // Calcular preço com desconto inicial
      const valor = produto.valor || 0;
      const desconto = produto.desconto || 0;
      const precoComDescontoInicial = valor - (valor * desconto / 100);
      setPrecoComDesconto(precoComDescontoInicial);

    }
  }, [produto, form]);


  // Não redireciona automaticamente - usuário permanece na página
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (isError && !modalState.isOpen) {
    return <div>Ocorreu um erro ao carregar o produto</div>;
  }

  if (!produto && !modalState.isOpen) {
    return <div>Produto não encontrado</div>;
  }

  const onSubmit: SubmitHandler<EditarProdutoDtoType> = (data) => {
    mutation.mutate({ data, id: (id as string) });
  };

  // Check if the mutation was successful using the Result pattern
  if (mutation.isSuccess && mutation.data?.data?.isSuccess !== false) {
    return <Navigate to={`/cadastros/produtos`} />;
  }

  return (
    <div>
      <FormProvider {...form}>
        <PageBreadcrumb items={produtoBreadcrumbItems(produto?.codigo)} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {/* Display server error from Result pattern if present */}
          {form.formState.errors.root?.serverError && (
            <div className="alert alert-danger">
              {form.formState.errors.root.serverError.message}
            </div>
          )}
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Editar produto</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Altere as informações do produto
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="phoenix-secondary"
                type="button"
                as={Link}
                to="/cadastros/produtos"
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                loadingPosition="start"
              >
                Salvar alterações
              </Button>
            </div>
          </div>


          <Row className="g-5">
            <Col xs={12} xl={8}>
              <h4 className="mb-3">Identificação do produto</h4>
              <Row className="g-2">
                <Form.Group
                  as={Col}
                  xs={2}
                  xl={2}
                  className="mb-5"
                  controlId="codigo"
                >
                  <Form.Control
                    placeholder="Código"
                    disabled
                    value={produto?.codigo}
                  />
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={10}
                  xl={10}
                  className="mb-5"
                  controlId="descricao"
                >
                  <Form.Control
                    placeholder="Descrição do produto"
                    {...form.register("descricao")}
                    required
                    isInvalid={!!form.formState.errors.descricao}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.descricao?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <h4 className="mb-3">Preços</h4>
              <Row className="g-3 mb-5">
                <Form.Group as={Col} xs={12} md={4} controlId="precoCompra">
                  <Form.Label className="text-body-highlight fw-bold">
                    Preço de Compra *
                  </Form.Label>
                  <Controller
                    name="precoCompra"
                    control={form.control}
                    rules={{
                      required: "Preço de compra é obrigatório",
                      min: { value: 0.01, message: "Preço de compra deve ser maior que zero" }
                    }}
                    render={({ field }) => (
                      <Money
                        {...field}
                        isInvalid={!!form.formState.errors.precoCompra}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e);
                          const precoCompra = floatFormat(e.target.value);
                          const margem = floatFormat(form.watch("margem"));
                          const valor = precoCompra * (1 + margem / 100);
                          form.setValue("valor", valor);
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.precoCompra?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={4} controlId="margem">
                  <Form.Label className="text-body-highlight fw-bold">
                    Margem (%) *
                  </Form.Label>
                  <Controller
                    name="margem"
                    control={form.control}
                    rules={{
                      required: "Margem é obrigatória",
                      min: { value: 0, message: "Margem deve ser maior ou igual a zero" }
                    }}
                    render={({ field }) => (
                      <Money
                        {...field}
                        isInvalid={!!form.formState.errors.margem}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e);
                          const precoCompra = floatFormat(form.watch("precoCompra"));
                          const margem = floatFormat(e.target.value);
                          const valor = precoCompra * (1 + margem / 100);
                          form.setValue("valor", valor);
                          const desconto = floatFormat(form.watch("desconto") ?? 0);
                          const novoPrecoComDesconto = valor - (valor * desconto / 100);
                          setPrecoComDesconto(novoPrecoComDesconto);
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.margem?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={4} controlId="valor">
                  <Form.Label className="text-body-highlight fw-bold">
                    Preço de Venda *
                  </Form.Label>
                  <Controller
                    name="valor"
                    control={form.control}
                    rules={{
                      required: "Preço de venda é obrigatório",
                      min: { value: 0.01, message: "Preço de venda deve ser maior que zero" }
                    }}
                    render={({ field }) => (
                      <Money
                        {...field}
                        isInvalid={!!form.formState.errors.valor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e);
                          const precoCompra = floatFormat(form.watch("precoCompra"));
                          const valor = floatFormat(e.target.value);
                          const margem = ((valor - precoCompra) / precoCompra) * 100;
                          form.setValue("margem", margem);
                          const desconto = floatFormat(form.watch("desconto") ?? 0);
                          const novoPrecoComDesconto = valor - (valor * desconto / 100);
                          setPrecoComDesconto(novoPrecoComDesconto);
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.valor?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={4} controlId="desconto">
                  <Form.Label className="text-body-highlight fw-bold">
                    Desconto Automático
                  </Form.Label>
                  <Controller
                    name="desconto"
                    control={form.control}
                    render={({ field }) => (
                      <Money
                        {...field}
                        isInvalid={!!form.formState.errors.desconto}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e);
                          const desconto = floatFormat(e.target.value);
                          const valor = floatFormat(form.watch("valor"));
                          const novoPrecoComDesconto = valor - (valor * desconto / 100);
                          setPrecoComDesconto(novoPrecoComDesconto);
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.desconto?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={4} controlId="descontoMaximo">
                  <Form.Label className="text-body-highlight fw-bold">
                    Desconto Máximo
                  </Form.Label>
                  <Controller
                    name="descontoMaximo"
                    control={form.control}
                    render={({ field }) => (
                      <Money {...field} isInvalid={!!form.formState.errors.descontoMaximo} />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.descontoMaximo?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={4} controlId="precoComDesconto">
                  <Form.Label className="text-body-highlight fw-bold">
                    Preço com Desconto
                  </Form.Label>
                  <Money
                    value={precoComDesconto}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const novoPrecoComDesconto = floatFormat(e.target.value);
                      const valor = floatFormat(form.watch("valor"));

                      if (valor > 0) {
                        const novoDesconto = ((valor - novoPrecoComDesconto) / valor) * 100;
                        const descontoFinal = Math.max(0, novoDesconto); // Garante que não seja negativo
                        form.setValue("desconto", descontoFinal);
                      }

                      setPrecoComDesconto(novoPrecoComDesconto);
                    }}
                  />
                </Form.Group>
                <Form.Group as={Col} xs={12} md={4} controlId="precoMarketplace">
                  <Form.Label className="text-body-highlight fw-bold">
                    Preço para Marketplace
                  </Form.Label>
                  <Controller
                    name="precoMarketplace"
                    control={form.control}
                    render={({ field }) => (
                      <Money
                        {...field}
                        isInvalid={!!form.formState.errors.precoMarketplace}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e);
                          const valor = floatFormat(e.target.value);
                          form.setValue("precoMarketplace", valor);
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.valor?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} md={4} controlId="precoAberto">
                  <Form.Label className="text-body-highlight fw-bold d-block">
                    &nbsp;
                  </Form.Label>
                  <Form.Check
                    type="switch"
                    id="precoAberto"
                    label="Preço Aberto"
                    {...form.register("precoAberto")}
                    isInvalid={!!form.formState.errors.precoAberto}
                    className="text-body-highlight fw-bold"
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.precoAberto?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} md={4} controlId="usoContinuo">
                  <Form.Label className="text-body-highlight fw-bold d-block">
                    &nbsp;
                  </Form.Label>
                  <Form.Check
                    type="switch"
                    id="usoContinuo"
                    label="Uso Contínuo"
                    {...form.register("usoContinuo")}
                    isInvalid={!!form.formState.errors.usoContinuo}
                    className="text-body-highlight fw-bold"
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.usoContinuo?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <div className="mb-5">
                <ProdutoEmpresaPrecosEspecificosTable produtoId={produtoId} />
              </div>

              <div>
                <h4 className="mb-3 h-100">Inventário</h4>
                <InventoryTab />
              </div>

              {/* Histórico de Alterações de Preço - Componente não invasivo */}
              <div className="mt-4">
                <HistoricoAlteracaoPrecoTab produtoId={produtoId} />
              </div>

              {/* Histórico de Movimentos de Estoque - Componente não invasivo */}
              <div className="mt-4">
                <HistoricoEstoqueTab produtoId={produtoId} />
              </div>

              {/* Histórico de Vendas - Componente não invasivo */}
              <div className="mt-4">
                <HistoricoVendasTab produtoId={produtoId} />
              </div>
            </Col>
            <Col xs={12} xl={4}>
              <Row className="g-2">
                <Col xs={12} xl={12}>
                  <OrganizeFormCard className="mb-3" />
                </Col>
                <Col xs={12} xl={12}>
                  <RegraFiscalEmpresaFormCard />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </div>
  );
};

export default EditarProduto;
